import { Check, KeyboardArrowDownRounded, Search } from "@mui/icons-material";
import {
  Autocomplete,
  autocompleteClasses,
  Box,
  Button,
  ButtonOwnProps,
  Icon,
  List,
  listClasses,
  Menu,
  MenuItem,
  outlinedInputClasses,
  TextField
} from "@mui/material";
import React, {
  Dispatch,
  SetStateAction,
  useMemo,
  useRef,
  useState
} from "react";
import { LanguageCard } from "..";
import { LanguageDataMap, LANGUAGES } from "@/constants/languages";
import LanguagePill from "@/components/elements/LanguagePill";
import { CREATE_PROJECT_COMPACT_VIEW_WIDTH } from "../../constants";

interface LanguageSelectorProps {
  exclude?: string[];
  selectorElement?: React.ReactNode | undefined;
  selectorElementProps?: ButtonOwnProps;
  onClick?: Dispatch<SetStateAction<any>> | (() => void);
  onMultiSelect?: Dispatch<SetStateAction<any>> | (() => void);
  disable?: boolean;
  optionsList: LANGUAGES[];
  defaultSelected?: LANGUAGES;
  selectedItems?: LANGUAGES[];
  multiSelect?: boolean;
}

const OptionCard = ({
  multiSelect,
  option,
  selected,
  onClick,
  ...props
}: {
  multiSelect: boolean;
  option: LANGUAGES;
  selected: boolean;
  onClick?: (e: any) => void;
}) => {
  return (
    <MenuItem
      key={option}
      selected={selected}
      onClick={onClick}
      {...props}
      sx={{
        px: 2,
        minHeight: 36,
        borderRadius: 0,
        [`&.${autocompleteClasses.option}`]: {
          py: 1
        },
        [`&.${autocompleteClasses.option}.${autocompleteClasses.focused}`]: {
          backgroundColor: (theme) => theme.palette.action.selected
        }
      }}
    >
      {multiSelect ? (
        <Box
          sx={{
            minWidth: 36,
            height: 20
          }}
        >
          {selected ? <Check sx={{ height: 20, width: 20 }} /> : null}
        </Box>
      ) : null}

      <LanguageCard
        language={option}
        onClick={() => {}}
        minimal
        listItemProps={{
          sx: {
            p: 0,
            overflow: "hidden"
          }
        }}
      />
    </MenuItem>
  );
};

export const LanguageSelector = ({
  exclude,
  selectorElement,
  selectorElementProps,
  onClick,
  onMultiSelect,
  disable = false,
  optionsList,
  defaultSelected,
  selectedItems = [],
  multiSelect = false
}: LanguageSelectorProps): React.ReactNode => {
  const OPTIONS = useMemo(
    () =>
      exclude?.length
        ? optionsList.filter((language) => !exclude?.includes(language))
        : optionsList,
    [exclude, optionsList]
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(defaultSelected ? -1 : 0);
  const open = Boolean(anchorEl);
  const listRef = useRef<HTMLInputElement>(null);
  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    if (disable) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (
    _: React.MouseEvent<HTMLElement>,
    option: LANGUAGES
  ) => {
    _.stopPropagation();
    setSelectedIndex(OPTIONS.findIndex((item) => item === option));
    if (!multiSelect) {
      setAnchorEl(null);
    }
    if (onClick && typeof onClick === "function") {
      onClick(option);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const language =
    defaultSelected ||
    OPTIONS[selectedIndex > OPTIONS.length - 1 ? 0 : selectedIndex];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%"
      }}
    >
      <List
        component="nav"
        sx={{
          p: 0,
          border: selectorElement || multiSelect ? "0" : `1px solid`,
          borderColor: "secondary.outlinedBorder",
          borderRadius: 2,
          backgroundColor: (theme) => theme.palette.background.paper,
          width: "100%",
          display: "flex",
          height: "100%"
        }}
        ref={listRef}
      >
        {selectorElement ? (
          <Button
            {...selectorElementProps}
            onClick={handleClickListItem}
            id="btn-add-languages"
          >
            {selectorElement}
          </Button>
        ) : multiSelect ? (
          <Autocomplete
            multiple
            id="language-selector"
            options={OPTIONS}
            getOptionLabel={(option) => LanguageDataMap[option]?.name}
            onChange={(_, value: LANGUAGES[]) => {
              if (onMultiSelect) {
                onMultiSelect(value);
              }
            }}
            value={selectedItems}
            ListboxProps={{
              sx: {
                minHeight: 160,
                maxHeight: 300
              }
            }}
            sx={(theme) => ({
              width: "100%",
              borderRadius: 2,
              backgroundColor: "background.paper",
              [`& .${autocompleteClasses.inputRoot}`]: {
                py: 1,
                gap: 1,
                border: 0,
                borderRadius: 2
              },
              [`&.${autocompleteClasses.root} .${outlinedInputClasses.root}`]: {
                border: "1px solid",
                borderColor: "secondary.outlinedBorder",
                maxHeight: 136, // shows 4 rows
                overflow: "auto",
                pl: 1.5,
                [theme.breakpoints.down(CREATE_PROJECT_COMPACT_VIEW_WIDTH)]: {
                  minHeight: 40
                },
                [theme.breakpoints.up(CREATE_PROJECT_COMPACT_VIEW_WIDTH)]: {
                  minHeight: 56
                }
              },
              [`& .${outlinedInputClasses.root}.${outlinedInputClasses.focused}`]:
                {
                  borderWidth: 2,
                  borderColor: "secondary.main",
                  [`& .${outlinedInputClasses.notchedOutline}`]: {
                    borderWidth: 0
                  }
                },
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderWidth: 0
              },
              [`& .${outlinedInputClasses.root} .${autocompleteClasses.input}`]:
                {
                  py: 0,
                  pl: 0,
                  minHeight: 24,
                  maxHeight: 300,
                  overflow: "auto"
                }
            })}
            disableCloseOnSelect
            renderInput={(params) => (
              <TextField
                {...params}
                color="secondary"
                placeholder={
                  selectedItems?.length ? "" : "Search target locales"
                }
                InputProps={{
                  ...params?.["InputProps"],
                  startAdornment: selectedItems?.length ? (
                    params?.["InputProps"]?.startAdornment
                  ) : (
                    <Search sx={{ height: 24, width: 24, mr: 1 }} />
                  )
                }}
              />
            )}
            renderTags={(locales: LANGUAGES[]) => {
              return locales.map((l) => (
                <LanguagePill
                  language={l}
                  key={l}
                  size="small"
                  variant="filled"
                  chipProps={{
                    onDelete: () => {
                      if (onClick) onClick(l as LANGUAGES);
                    }
                  }}
                />
              ));
            }}
            renderOption={(props: object, option: LANGUAGES) => {
              return (
                <OptionCard
                  {...{
                    multiSelect,
                    option,
                    selected: selectedItems.includes(option),
                    ...props
                  }}
                />
              );
            }}
            noOptionsText="Couldn’t find the language you’re looking for"
          />
        ) : (
          <LanguageCard
            language={language}
            onClick={handleClickListItem}
            listItemProps={{
              sx: {
                px: 2,
                py: 1,
                borderRadius: 2,
                cursor: disable ? "default" : "pointer",
                minHeight: 68
              }
            }}
            rightChild={
              disable ? null : (
                <Icon
                  sx={{
                    ml: 3
                  }}
                >
                  <KeyboardArrowDownRounded />
                </Icon>
              )
            }
          />
        )}
      </List>
      {multiSelect ? null : (
        <Menu
          id="language-selector"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            role: "listbox"
          }}
          sx={{
            width: "100%",
            "& .MuiMenu-paper": {
              width: listRef?.current?.clientWidth
            },
            [`& .${listClasses.root}`]: {
              px: 0,
              py: 1,
              minHeight: 24,
              maxHeight: 300,
              overflow: "auto"
            }
          }}
        >
          {OPTIONS.map((option) => (
            <OptionCard
              key={option}
              {...{
                multiSelect,
                onClick: (event: any) => handleMenuItemClick(event, option),
                option,
                selected: selectedItems.includes(option)
              }}
            />
          ))}
        </Menu>
      )}
    </Box>
  );
};
